import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import Footer from './Footer';
import Loader from '../common/Loader.js';
import DocumentModal from '../common/DocumentModal';
import axios from '../../config/axios';
import ShipmentTrackingPanel from '../tracking/ShipmentTrackingPanel';
import TrackCaptcha from './TrackCaptcha';
import { buildPublicTrackPath, resolvePublicTrackShipmentId } from '../../utils/publicTrackRoute.js';
import { Package, Search, Eye, ArrowLeft, Truck, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const NAVBAR_HEIGHT = 80;
const TRACK_RESULT_STATE_KEY = 'publicTrackResult';
const TRACK_ACCESS_STATE_KEY = 'publicTrackAccessToken';

const PublicShipmentTracking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { shipmentId: routeShipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const [shipmentId, setShipmentId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnId, setReturnId] = useState(null);
  const [loadingReturnId, setLoadingReturnId] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [deliveryImageModal, setDeliveryImageModal] = useState(false);
  const [selectedDeliveryImage, setSelectedDeliveryImage] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const hydratedTrackResultRef = useRef(false);

  const refreshCaptcha = useCallback(() => {
    setCaptchaAnswer('');
    setCaptchaToken('');
    setCaptchaVerified(false);
    setCaptchaKey((k) => k + 1);
  }, []);

  const applyTrackSuccess = useCallback((payload, fallbackId = '') => {
    const trackKey = String(
      payload?.track_key ||
        payload?.shipment_details?.consignment_no ||
        fallbackId
    );
    const token = payload?.access_token || '';
    setTrackingData(payload);
    setAccessToken(token);
    if (token) {
      sessionStorage.setItem(`track_access_${trackKey}`, token);
      const gc = payload?.shipment_details?.shipment_id;
      if (gc) sessionStorage.setItem(`track_access_${gc}`, token);
    }
    if (trackKey) setShipmentId(trackKey);
    return { trackKey, token };
  }, []);

  // Restore results after /track-shipment → /:id navigation remounts this page
  useEffect(() => {
    if (hydratedTrackResultRef.current) return;
    const payload = location.state?.[TRACK_RESULT_STATE_KEY];
    if (!payload?.success) return;

    hydratedTrackResultRef.current = true;
    applyTrackSuccess(payload, resolvePublicTrackShipmentId(routeShipmentId, searchParams));
    const token = location.state?.[TRACK_ACCESS_STATE_KEY] || payload?.access_token || '';
    if (token) setAccessToken(token);

    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate, applyTrackSuccess, routeShipmentId, searchParams]);

  const trackShipment = useCallback(async (rawId, { updateUrl = false, token, answer } = {}) => {
    const id = String(rawId || '').trim();
    if (!id) {
      setError('Please enter a consignment number or GC number');
      return;
    }

    const captchaTok = token ?? captchaToken;
    const captchaAns = String(answer ?? captchaAnswer).trim();
    if (!captchaTok || !captchaAns || captchaAns.length !== 5) {
      setError('Please enter the 5-character security code before tracking');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setAccessToken('');
    setReturnId(null);

    try {
      const response = await axios.get(`/tracking/${encodeURIComponent(id)}`, {
        headers: {
          'X-Captcha-Token': captchaTok,
          'X-Captcha-Answer': captchaAns,
        },
      });
      if (response.data.success) {
        const { trackKey, token: issuedToken } = applyTrackSuccess(response.data, id);
        const nextPath = buildPublicTrackPath(trackKey);
        // Navigating from /track-shipment to /:id remounts this component and used to
        // wipe results. Pass payload in location state so the new mount can restore it.
        if (updateUrl && nextPath && nextPath !== location.pathname) {
          navigate(nextPath, {
            replace: true,
            state: {
              [TRACK_RESULT_STATE_KEY]: response.data,
              [TRACK_ACCESS_STATE_KEY]: issuedToken,
            },
          });
          // Fresh captcha mounts on the new page — skip refresh on this instance
        } else {
          if (updateUrl && nextPath) {
            navigate(nextPath, { replace: true, state: null });
          }
          refreshCaptcha();
        }
      } else {
        setError(response.data.error || 'Shipment not found');
        refreshCaptcha();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking information');
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }, [navigate, captchaToken, captchaAnswer, refreshCaptcha, applyTrackSuccess, location.pathname]);

  // Prefill from URL / QR — user must still pass captcha before tracking
  useEffect(() => {
    const resolvedId = resolvePublicTrackShipmentId(routeShipmentId, searchParams);
    if (!resolvedId) return;
    setShipmentId(resolvedId);
  }, [routeShipmentId, searchKey, searchParams]);

  useEffect(() => {
    if (trackingData?.shipment_details?.status?.toLowerCase() === 'returned') {
      fetchReturnId(trackingData.shipment_details.shipment_id);
    }
  }, [trackingData]);

  const fetchReturnId = async (id) => {
    try {
      setLoadingReturnId(true);
      const response = await axios.get('/shipments/returned', {
        params: { return_status: 'close' },
      });

      if (response.data.success && response.data.data?.length > 0) {
        const returns = response.data.data.filter((r) => r.shipment_id === id);
        if (returns.length > 0) {
          const sortedReturns = returns.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setReturnId(sortedReturns[0].return_id);
        }
      }
    } catch (err) {
      console.error('Error fetching return ID:', err);
      setReturnId(null);
    } finally {
      setLoadingReturnId(false);
    }
  };

  const handleViewDeliveryImage = (imageUrl) => {
    setSelectedDeliveryImage(imageUrl);
    setDeliveryImageModal(true);
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    await trackShipment(shipmentId, {
      updateUrl: true,
      token: captchaToken,
      answer: captchaAnswer,
    });
  };

  const handleViewReturn = () => {
    if (returnId) {
      navigate(`/public-view-return/${returnId}`);
    }
  };

  const trackKey =
    trackingData?.track_key ||
    trackingData?.shipment_details?.consignment_no ||
    trackingData?.shipment_details?.shipment_id;

  const handleViewShipment = async () => {
    if (!trackKey) return;
    const token =
      accessToken ||
      sessionStorage.getItem(`track_access_${trackKey}`) ||
      '';
    try {
      const response = await axios.get(`/tracking/${encodeURIComponent(trackKey)}/view`, {
        headers: token ? { 'X-Track-Access-Token': token } : {},
      });
      if (response.data.success) {
        navigate(`/view-shipment/${encodeURIComponent(trackKey)}`, {
          state: { shipment: response.data.shipment, accessToken: token },
        });
      } else {
        setError(response.data.error || 'Unable to open shipment');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to open shipment. Please track again.');
    }
  };

  const handleDownloadInvoice = async () => {
    if (!trackKey) return;
    const token =
      accessToken ||
      sessionStorage.getItem(`track_access_${trackKey}`) ||
      '';
    try {
      setDownloadingInvoice(true);
      const response = await axios.get(`/tracking/${encodeURIComponent(trackKey)}/invoice`, {
        headers: token ? { 'X-Track-Access-Token': token } : {},
        responseType: 'blob',
        params: { include_labour: 'yes' },
      });
      const id = trackingData?.shipment_details?.shipment_id || trackKey;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download invoice. Please track again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const isReturnedStatus = trackingData?.shipment_details?.status?.toLowerCase() === 'returned';

  const headerActions = trackingData ? (
    <>
      <button
        type="button"
        onClick={handleViewShipment}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-700 text-white rounded-full text-sm font-semibold hover:bg-purple-800 transition-all shadow-sm"
      >
        <Eye className="w-4 h-4 shrink-0" />
        View Shipment
      </button>
      <button
        type="button"
        onClick={handleDownloadInvoice}
        disabled={downloadingInvoice}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-purple-700 border border-purple-200 rounded-full text-sm font-semibold hover:bg-purple-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Download className="w-4 h-4 shrink-0" />
        {downloadingInvoice ? 'Downloading...' : 'Download Invoice'}
      </button>
      {isReturnedStatus && returnId && (
        <button
          type="button"
          onClick={handleViewReturn}
          disabled={loadingReturnId}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-700 text-white rounded-full text-sm font-semibold hover:bg-indigo-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Eye className="w-4 h-4 shrink-0" />
          {loadingReturnId ? t('common.loading') : 'View Return'}
        </button>
      )}
    </>
  ) : null;

  return (
    <div className="min-h-screen bg-purple-50/40 flex flex-col">
      <section
        className="relative overflow-hidden flex-1"
        style={{ paddingTop: NAVBAR_HEIGHT }}
      >
        {/* Soft purple landing background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-purple-50/70" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `
                linear-gradient(to right, #7c3aed 1px, transparent 1px),
                linear-gradient(to bottom, #7c3aed 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.12),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.12),transparent_55%)]" />
        </div>

        <div className="relative container mx-auto px-4 py-8 sm:py-10 md:py-12">
          {/* Header — matches Features / Business Login style */}
          <div className="max-w-3xl mx-auto text-center mb-6 sm:mb-8 md:mb-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-purple-100 bg-white/80 text-purple-700 hover:bg-purple-50 transition-colors shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <p
                className="
                  inline-block text-xs sm:text-sm uppercase tracking-widest
                  bg-gradient-to-r from-purple-100 via-purple-50 to-indigo-100
                  px-3 sm:px-4 py-1.5 rounded-full
                  font-semibold text-purple-700
                "
              >
                Live Tracking
              </p>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800 leading-tight flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
              <span className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 text-purple-700 shrink-0">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
              </span>
              <span
                className="
                  bg-gradient-to-r from-pink-800 via-purple-600 to-indigo-600
                  bg-clip-text text-transparent
                "
              >
                Track Your Shipment
              </span>
            </h1>

            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600 leading-relaxed px-1">
              Enter your consignment number or GC number for real-time delivery updates across mobile, tablet, and desktop.
            </p>
          </div>

          {/* Search card */}
          <div
            className="
              max-w-4xl mx-auto mb-6 sm:mb-8
              rounded-2xl p-4 sm:p-5 md:p-6
              bg-white/80 backdrop-blur-md
              border border-purple-100
              shadow-lg
            "
          >
            <form
              onSubmit={(e) => {
                if (!captchaVerified || !captchaToken || String(captchaAnswer).trim().length !== 5) {
                  e.preventDefault();
                  setError('Please enter the correct 5-character security code');
                  return;
                }
                handleTrack(e);
              }}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                <div className="relative flex-1 min-w-0">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400 pointer-events-none" />
                  <input
                    type="text"
                    value={shipmentId}
                    onChange={(e) => setShipmentId(e.target.value)}
                    placeholder="Consignment No or GC Number"
                    className="
                      w-full min-w-0 h-11 pl-10 pr-3 sm:pr-4
                      text-sm sm:text-base
                      border border-purple-100 rounded-xl bg-white
                      focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200
                      transition-all placeholder:text-gray-400
                    "
                  />
                </div>

                <TrackCaptcha
                  key={captchaKey}
                  answer={captchaAnswer}
                  onAnswerChange={setCaptchaAnswer}
                  onTokenChange={setCaptchaToken}
                  onVerified={setCaptchaVerified}
                  disabled={loading}
                />

                <button
                  type="submit"
                  disabled={loading || !captchaVerified || !captchaToken || !String(shipmentId).trim()}
                  className="
                    w-full lg:w-auto lg:min-w-[120px] h-11
                    inline-flex items-center justify-center gap-2
                    bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600
                    hover:shadow-md hover:brightness-105
                    text-white px-5 rounded-full font-semibold
                    transition-all disabled:opacity-45 disabled:cursor-not-allowed shadow-sm
                    text-sm sm:text-base shrink-0
                  "
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Tracking...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 shrink-0" />
                      Track
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="max-w-5xl mx-auto w-full min-w-0">
            {loading && (
              <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-purple-100 shadow-lg p-4 sm:p-6 mb-4">
                <Loader fullScreen={false} size="default" message="Tracking your shipment..." />
              </div>
            )}

            {!loading && error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-3 sm:p-4 mb-4 shadow-sm">
                <p className="text-red-800 font-medium text-sm sm:text-base break-words">{error}</p>
              </div>
            )}

            {!loading && trackingData && (
              <div className="rounded-2xl overflow-hidden border border-purple-100 shadow-lg bg-white/90 backdrop-blur-md">
                <ShipmentTrackingPanel
                  trackingData={trackingData}
                  onViewDeliveryImage={handleViewDeliveryImage}
                  headerActions={headerActions}
                />
              </div>
            )}

            {!trackingData && !loading && !error && (
              <div
                className="
                  bg-white/80 backdrop-blur-md rounded-2xl
                  border border-purple-100 shadow-lg
                  p-6 sm:p-8 md:p-10 text-center min-w-0
                "
              >
                <div className="mx-auto mb-4 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
                  <Package className="w-7 h-7 sm:w-8 sm:h-8 text-purple-500" />
                </div>
                <h3
                  className="
                    text-lg sm:text-xl font-semibold mb-2
                    bg-gradient-to-r from-pink-800 via-purple-600 to-indigo-600
                    bg-clip-text text-transparent
                  "
                >
                  Track Your Shipment
                </h3>
                <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                  Enter a consignment number or GC number, type the 5-character security code, then tap Track to view real-time status and route updates.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />

      <DocumentModal
        isOpen={deliveryImageModal}
        onClose={() => setDeliveryImageModal(false)}
        documentUrl={selectedDeliveryImage || ''}
        documentType="Government ID proof"
      />
    </div>
  );
};

export default PublicShipmentTracking;
